// ===========================================
// Progress By Level Chart Component
// ===========================================
// Pie chart showing student distribution by program level

import { Card, CardContent, CardHeader, Typography, Box, Divider, CircularProgress } from '@mui/material';
import { School as SchoolIcon } from '@mui/icons-material';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from 'recharts';

import { StudentProgressByLevel } from '../../api/reports.api';

// ===========================================
// TYPES
// ===========================================

interface ProgressByLevelChartProps {
  data: StudentProgressByLevel[];
  loading?: boolean;
}

// ===========================================
// CONSTANTS
// ===========================================

const COLORS = ['#4580E4', '#FFCE00', '#96DAC9', '#FFAE9E', '#a3d9f6', '#9DA5AF'];

// ===========================================
// CUSTOM TOOLTIP
// ===========================================

function CustomTooltip({ active, payload }: any) {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;

  return (
    <Card sx={{ p: 1.5, minWidth: 220 }}>
      <Typography variant="body2" fontWeight={600} gutterBottom>
        {data.programLevelName}
      </Typography>
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption">Students:</Typography>
          <Typography variant="caption" fontWeight={600}>
            {data.studentCount}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption">Avg Progress:</Typography>
          <Typography variant="caption" fontWeight={600}>
            {data.averageProgressRate.toFixed(1)}%
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption">Avg Attendance:</Typography>
          <Typography variant="caption" fontWeight={600}>
            {data.averageAttendanceRate.toFixed(1)}%
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption">Pieces Completed:</Typography>
          <Typography variant="caption" fontWeight={600}>
            {data.totalPiecesCompleted}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
}

// ===========================================
// COMPONENT
// ===========================================

export default function ProgressByLevelChart({ data, loading }: ProgressByLevelChartProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SchoolIcon color="primary" />
              <Typography variant="h6">Students by Program Level</Typography>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SchoolIcon color="primary" />
              <Typography variant="h6">Students by Program Level</Typography>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No level distribution data available
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SchoolIcon color="primary" />
            <Typography variant="h6">Students by Program Level</Typography>
          </Box>
        }
        subheader={`${data.reduce((sum, item) => sum + item.studentCount, 0)} total students`}
      />
      <Divider />
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <PieChart>
            <Pie
              data={data}
              dataKey="studentCount"
              nameKey="programLevelName"
              cx="50%"
              cy="50%"
              outerRadius={120}
              label={(entry: any) => `${entry.programLevelName}: ${entry.studentCount}`}
            >
              {data.map((_entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
